import gql from 'graphql-tag';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
// @ts-expect-error
import BasicFolderItem from 'basic-folder-item/BasicFolderItem';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ComplexPolymerHistoryPanelState} from 'FileStates';
import CloneModal from 'folder-items/CloneModal';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as ComplexPolymerActions from 'modbio/complex-polymers/ComplexPolymerActions';
import {updateComplexPolymerVersion} from 'modbio/redux/ComplexPolymerReduxActions';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import ReactPanelBaseView, {Subpanel} from 'toolbar/ReactPanelBaseView';
import HistoryPanelComponent from 'toolbar/shared/HistoryPanelComponent';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import runModal from 'ui-library/runModal';

import {
  ComplexPolymerVersionQuery,
  ComplexPolymerVersionQueryVariables,
} from './__generated/ComplexPolymerVersionQuery';

type OligoConjugateVersionGraphQL = NonNullable<
  ExtractByTypename<ComplexPolymerVersionQuery['complexPolymer'], 'OligoConjugateNode'>['versions']
>[number];

type OligoDuplexVersionGraphQL = NonNullable<
  ExtractByTypename<ComplexPolymerVersionQuery['complexPolymer'], 'OligoDuplexNode'>['versions']
>[number];

type ComplexPolymerVersionGraphQL = NonNullable<
  ExtractByTypename<ComplexPolymerVersionQuery['complexPolymer'], 'ComplexPolymerNode'>['versions']
>[number];

type VersionGraphQL = OligoConjugateVersionGraphQL | OligoDuplexVersionGraphQL | ComplexPolymerVersionGraphQL;

export const COMPLEX_POLYMER_VERSION_QUERY = gql`
  query ComplexPolymerVersionQuery($complexPolymerId: StrictString!)
  @skipAudit
  @engComponent(name: MODIFIED_BIOLOGICS) {
    complexPolymer(id: $complexPolymerId) {
      ... on OligoConjugateNode {
        id
      }
      ... on OligoDuplexNode {
        id
      }
      ... on ComplexPolymerNode {
        id
      }
      ... on ComplexPolymerInterface {
        versions {
          id
          name
          creator {
            id
            name
          }
          modifiedAt
          parent {
            id
          }
        }
        currentVersion {
          id
          name
          creator {
            id
            name
          }
          modifiedAt
          parent {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ComplexPolymerHistoryPanelProps {
  id: string;
  name: string;
  permissions?: Permissions;
  setIsLatest: (isLatest: boolean) => void;
  folderId: string;
}

function ComplexPolymerHistoryPanel(props: ComplexPolymerHistoryPanelProps): JSX.Element {
  const openComplexPolymerVersionId = useSelector(
    (state: BenchlingState) => state.ui.modbio.complexPolymerVersionUIs[props.id].complexPolymerVersionId
  );

  const dispatch = useDispatch<BenchlingDispatch>();

  const openVersion = (versionId: string, currentVersionId: string): void => {
    dispatch(updateComplexPolymerVersion(props.id, versionId));
    props.setIsLatest(versionId === currentVersionId);
  };

  const resetToVersion = (currentVersionId: string): void => {
    ComplexPolymerActions.resetComplexPolymerVersion(props.id, openComplexPolymerVersionId, currentVersionId);
  };

  const cloneFromVersion = (): void => {
    runModal(CloneModal, {
      modelType: ModelTypeConstants.BASIC_FOLDER_ITEM,
      id: props.id,
      versionId: openComplexPolymerVersionId,
      initialName: `${props.name} (new)`,
      initialFolderId: props.folderId,
    });
  };

  return (
    <BenchlingQuery<ComplexPolymerVersionQuery, ComplexPolymerVersionQueryVariables>
      query={COMPLEX_POLYMER_VERSION_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{complexPolymerId: props.id}}
    >
      {(data) => {
        assert(!isInaccessible(data.complexPolymer), 'Complex polymer must be accessible.');
        assert(
          data.complexPolymer.currentVersion != null,
          'Expected complex polymer to have current version'
        );

        const currentVersion = data.complexPolymer.currentVersion;
        return (
          <HistoryPanelComponent
            openVersion={(versionId: string) => {
              openVersion(versionId, currentVersion.id);
            }}
            resetToVersion={() => {
              resetToVersion(currentVersion.id);
            }}
            versions={data.complexPolymer.versions!.map((version: VersionGraphQL) => ({
              ...version,
              parentId: version.parent?.id ?? null,
              fetching: false,
            }))}
            cloneFromVersion={cloneFromVersion}
            openVersionId={openComplexPolymerVersionId}
            isLatest={currentVersion.id === openComplexPolymerVersionId}
            unsaved={false}
            showNames={true}
            showSeconds={true}
            hasParent={false}
            parent={null}
            permissions={props.permissions}
          />
        );
      }}
    </BenchlingQuery>
  );
}

class ComplexPolymerHistoryPanelView extends ReactPanelBaseView<
  ComplexPolymerHistoryPanelState,
  BasicFolderItem
> {
  getSubpanels(): Array<Subpanel> {
    return [
      {
        key: 'history',
        title: 'History',
        component: (
          <ComplexPolymerHistoryPanel
            id={this.model.id}
            name={this.model.name()}
            permissions={this.model.permissions()}
            setIsLatest={(isLatest: boolean) => {
              this.model.setIsLatest(isLatest);
            }}
            folderId={this.model.folder().id}
          />
        ),
      },
    ];
  }
}

export default ComplexPolymerHistoryPanelView;
